import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Home, Brain, ListTodo, BarChart3, FileText, Video,
  Lightbulb, HelpCircle, LogOut, User, Moon, Sun, Menu, X, Watch, Clock, Sparkles, BookOpen, FlaskConical, ClipboardList, Activity
} from 'lucide-react';

// Logo URLs
const SYMBOL_LOGO = "https://customer-assets.emergentagent.com/job_neuro-care-hub-2/artifacts/zntnld86_Lifely%20Symbol%20AI.png";
const TEXT_LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_neuro-care-hub-2/artifacts/yxqxnzye_ReGenflo%20logo%202%20contrast.png";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: Home },
  { path: '/daily', label: 'Daily Alignment', icon: Clock },
  { path: '/mindflo', label: 'MindFlō', icon: Sparkles },
  { path: '/twinflo', label: 'TwinFlō', icon: Activity },
  { path: '/decode', label: 'My DECODE', icon: Brain },
  { path: '/protocol', label: 'My Pathway', icon: ListTodo },
  { path: '/tracking', label: 'Tracking', icon: BarChart3 },
  { path: '/wearables', label: 'Wearables', icon: Watch },
  { path: '/my-intake', label: 'My Intake', icon: ClipboardList },
  { path: '/my-labs', label: 'My Labs', icon: FlaskConical },
  { path: '/my-reports', label: 'My Reports', icon: FileText },
  { path: '/knowledge', label: 'Learn', icon: BookOpen },
  { path: '/support', label: 'Support & Guidance', icon: HelpCircle },
];

export default function SubscriberLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      {/* Top Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0B0F19]/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src={SYMBOL_LOGO} alt="Lifely" className="h-9 w-auto" />
              <img src={TEXT_LOGO_LIGHT} alt="ReGenflō" className="h-7 w-auto hidden sm:block" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`text-sm ${isActive 
                        ? 'text-[#C89B7B] bg-[#C89B7B]/10' 
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="w-4 h-4 mr-1.5" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-white/70 hover:text-white hover:bg-white/5"
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-white/70 hover:text-white hover:bg-white/5" data-testid="user-menu">
                    <div className="w-8 h-8 rounded-full bg-[#C89B7B]/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#C89B7B]" />
                    </div>
                    <span className="hidden sm:block text-sm">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#131B2B] border-white/10">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-white/50">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem className="text-white/70 focus:text-white focus:bg-white/5" data-testid="logout-btn" onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-white/70 hover:text-white hover:bg-white/5"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/10 bg-[#0B0F19]">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}>
                    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${isActive 
                      ? 'text-[#C89B7B] bg-[#C89B7B]/10' 
                      : 'text-white/70'
                    }`}>
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        {children}
      </main>
    </div>
  );
}
